import { Box, CssVarsProvider, useColorScheme } from "@mui/joy";
import { useState } from "react";
import CookieConsent from "react-cookie-consent";
import BookContent from "./components/BookContent";
import BookSelector from "./components/BookSelector";
import Footer from "./components/Footer";
import Header from "./components/Header";
import ModeToggle from "./components/ModeToggle";
import theme from "./theme";

export type BookMode = "sapphire-shore" | "ember-tide" | "third-book";

function AppContent() {
  const [selectedBook, setSelectedBook] = useState<BookMode>("sapphire-shore");
  const { mode } = useColorScheme();

  // Get book-specific colors
  const getBookColors = (book: BookMode) => {
    switch (book) {
      case "sapphire-shore":
        return {
          primary: "rgba(59, 130, 246, 0.15)", // Blue
          secondary: "rgba(120, 119, 198, 0.1)",
          accent: "rgba(147, 197, 253, 0.08)",
        };
      case "ember-tide":
        return {
          primary: "rgba(239, 68, 68, 0.15)", // Red/Orange
          secondary: "rgba(251, 146, 60, 0.1)",
          accent: "rgba(254, 202, 202, 0.08)",
        };
      case "third-book":
        return {
          primary: "rgba(168, 85, 247, 0.15)", // Purple
          secondary: "rgba(139, 92, 246, 0.1)",
          accent: "rgba(196, 181, 253, 0.08)",
        };
      default:
        return {
          primary: "rgba(120, 119, 198, 0.15)",
          secondary: "rgba(120, 119, 198, 0.1)",
          accent: "rgba(120, 119, 198, 0.08)",
        };
    }
  };

  const bookColors = getBookColors(selectedBook);
  const isDark = mode === "dark";

  // Get the main accent color for the selected book
  const getBookAccentColor = (book: BookMode) => {
    switch (book) {
      case "sapphire-shore":
        return "rgba(59, 130, 246, 0.8)";
      case "ember-tide":
        return "rgba(239, 68, 68, 0.8)";
      case "third-book":
        return "rgba(168, 85, 247, 0.8)";
      default:
        return "rgba(120, 119, 198, 0.8)";
    }
  };
  const bookAccentColor = getBookAccentColor(selectedBook);

  // Base background colors for light/dark mode - changes based on selected book
  const getBaseBackground = () => {
    switch (selectedBook) {
      case "sapphire-shore":
        return isDark
          ? "linear-gradient(135deg, #0a0a0a 0%, #1a1a2e 50%, #16213e 100%)"
          : "linear-gradient(135deg, #f8fafc 0%, #e2e8f0 50%, #cbd5e1 100%)";
      case "ember-tide":
        return isDark
          ? "linear-gradient(135deg, #0a0a0a 0%, #2e1a1a 50%, #3e1616 100%)"
          : "linear-gradient(135deg, #fef2f2 0%, #fee2e2 50%, #fecaca 100%)";
      case "third-book":
        return isDark
          ? "linear-gradient(135deg, #0a0a0a 0%, #2e1a2e 50%, #3e163e 100%)"
          : "linear-gradient(135deg, #faf5ff 0%, #f3e8ff 50%, #e9d5ff 100%)";
      default:
        return isDark
          ? "linear-gradient(135deg, #0a0a0a 0%, #1a1a2e 50%, #16213e 100%)"
          : "linear-gradient(135deg, #f8fafc 0%, #e2e8f0 50%, #cbd5e1 100%)";
    }
  };

  return (
    <Box
      sx={{
        minHeight: "100vh",
        background: getBaseBackground(),
        color: isDark ? "white" : "#1e293b",
        position: "relative",
        overflow: "hidden",
        transition: "all 0.8s cubic-bezier(0.4, 0, 0.2, 1)",
      }}
    >
      {/* Dynamic magical background elements */}
      <Box
        sx={{
          position: "fixed",
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          background: `
            radial-gradient(circle at 20% 80%, ${bookColors.primary} 0%, transparent 50%),
            radial-gradient(circle at 80% 20%, ${bookColors.secondary} 0%, transparent 50%),
            radial-gradient(circle at 40% 40%, ${bookColors.accent} 0%, transparent 50%)
          `,
          zIndex: -1,
          transition: "all 0.8s cubic-bezier(0.4, 0, 0.2, 1)",
        }}
      />

      {/* Fantasy floating particles */}
      <Box
        sx={{
          position: "fixed",
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          zIndex: -1,
          pointerEvents: "none",
        }}
      >
        {/* Floating orbs */}
        {[...Array(6)].map((_, i) => (
          <Box
            key={`orb-${i}`}
            sx={{
              position: "absolute",
              width: `${Math.random() * 8 + 4}px`,
              height: `${Math.random() * 8 + 4}px`,
              borderRadius: "50%",
              background: `radial-gradient(circle, ${bookColors.primary}80 0%, transparent 70%)`,
              left: `${Math.random() * 100}%`,
              top: `${Math.random() * 100}%`,
              animation: `float-${i % 3} ${
                8 + Math.random() * 4
              }s ease-in-out infinite`,
              animationDelay: `${Math.random() * 2}s`,
              "@keyframes float-0": {
                "0%, 100%": { transform: "translateY(0px) translateX(0px)" },
                "33%": { transform: "translateY(-20px) translateX(10px)" },
                "66%": { transform: "translateY(10px) translateX(-5px)" },
              },
              "@keyframes float-1": {
                "0%, 100%": { transform: "translateY(0px) translateX(0px)" },
                "50%": { transform: "translateY(-15px) translateX(-10px)" },
              },
              "@keyframes float-2": {
                "0%, 100%": { transform: "translateY(0px) translateX(0px)" },
                "25%": { transform: "translateY(15px) translateX(8px)" },
                "75%": { transform: "translateY(-10px) translateX(-12px)" },
              },
            }}
          />
        ))}

        {/* Mystical lines */}
        {[...Array(3)].map((_, i) => (
          <Box
            key={`line-${i}`}
            sx={{
              position: "absolute",
              width: "1px",
              height: `${100 + Math.random() * 200}px`,
              background: `linear-gradient(180deg, transparent 0%, ${bookColors.secondary}60 50%, transparent 100%)`,
              left: `${20 + i * 30}%`,
              top: `${Math.random() * 50}%`,
              animation: `shimmer ${
                6 + Math.random() * 3
              }s ease-in-out infinite`,
              animationDelay: `${i * 2}s`,
              "@keyframes shimmer": {
                "0%, 100%": { opacity: 0.3, transform: "scaleY(1)" },
                "50%": { opacity: 0.8, transform: "scaleY(1.2)" },
              },
            }}
          />
        ))}

        {/* Constellation dots */}
        {[...Array(12)].map((_, i) => (
          <Box
            key={`star-${i}`}
            sx={{
              position: "absolute",
              width: "2px",
              height: "2px",
              borderRadius: "50%",
              background: isDark
                ? "rgba(255, 255, 255, 0.6)"
                : "rgba(0, 0, 0, 0.3)",
              left: `${Math.random() * 100}%`,
              top: `${Math.random() * 100}%`,
              animation: `twinkle ${
                2 + Math.random() * 3
              }s ease-in-out infinite`,
              animationDelay: `${Math.random() * 2}s`,
              "@keyframes twinkle": {
                "0%, 100%": { opacity: 0.3, transform: "scale(1)" },
                "50%": { opacity: 1, transform: "scale(1.5)" },
              },
            }}
          />
        ))}
      </Box>

      <ModeToggle />

      <Box
        sx={{
          position: "relative",
          zIndex: 1,
          maxWidth: "1400px",
          margin: "0 auto",
          padding: "0 20px",
        }}
      >
        <Header accentColor={bookAccentColor} />
        <BookSelector
          selectedBook={selectedBook}
          onBookSelect={setSelectedBook}
        />
        <BookContent selectedBook={selectedBook} />
      </Box>

      <Footer />

      <CookieConsent
        enableDeclineButton
        acceptOnScrollPercentage={5}
        style={{
          background: isDark
            ? "rgba(0, 0, 0, 0.9)"
            : "rgba(255, 255, 255, 0.9)",
          backdropFilter: "blur(10px)",
          border: `1px solid ${
            isDark ? "rgba(255, 255, 255, 0.1)" : "rgba(0, 0, 0, 0.1)"
          }`,
          color: isDark ? "white" : "#1e293b",
        }}
        buttonStyle={{
          background: `linear-gradient(45deg, ${bookColors.primary}cc, ${bookColors.secondary}cc)`,
          color: "white",
          border: "none",
          borderRadius: "8px",
          padding: "10px 20px",
          fontFamily: "'Cinzel', serif",
        }}
        declineButtonStyle={{
          background: "transparent",
          color: isDark ? "white" : "#1e293b",
          border: `1px solid ${
            isDark ? "rgba(255, 255, 255, 0.3)" : "rgba(0, 0, 0, 0.3)"
          }`,
          borderRadius: "8px",
          padding: "10px 20px",
          fontFamily: "'Cinzel', serif",
        }}
      >
        This website uses cookies to enhance your journey through Osgara.
      </CookieConsent>
    </Box>
  );
}

export default function App() {
  return (
    <CssVarsProvider theme={theme}>
      <AppContent />
    </CssVarsProvider>
  );
}
